export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  suggestedName?: string;
}

export const MAX_SAFE_FILE_NAME_LENGTH = 80;

// Caracteres conflictivos para Supabase Storage / S3 que provocan error "invalid key"
const FORBIDDEN_FILE_NAME_CHARS_REGEX = /[+&%#?={}[\]|<>:*"\\/`^~$,;!@]/;

/**
 * Sanitiza un nombre de archivo para que sea 100% seguro en rutas de Supabase Storage / S3.
 * Elimina caracteres como '+', acentos, espacios y símbolos conflictivos, y limita la longitud.
 */
export function sanitizeToSafeFileName(fileName: string): string {
  const trimmed = `${fileName ?? ''}`.trim();
  if (!trimmed) return 'documento.pdf';

  // Separar nombre base y extensión
  const lastDot = trimmed.lastIndexOf('.');
  let baseName = lastDot > 0 ? trimmed.slice(0, lastDot) : trimmed;
  const ext = lastDot > 0 ? trimmed.slice(lastDot).toLowerCase() : '';

  // Normalizar acentos y diacríticos (ej. 'acta_reunión' -> 'acta_reunion')
  baseName = baseName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Reemplazar caracteres prohibidos, '+', espacios y símbolos por guiones
  baseName = baseName
    .replace(/[+&%#?={}[\]|<>:*"\\/`^~$,;!@\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!baseName) {
    baseName = 'documento';
  }

  // Garantizar que la longitud total con la extensión no supere el límite
  const maxBaseLength = Math.max(10, MAX_SAFE_FILE_NAME_LENGTH - ext.length);
  if (baseName.length > maxBaseLength) {
    baseName = baseName.slice(0, maxBaseLength).replace(/-+$/, '');
  }

  return `${baseName}${ext}`;
}

/**
 * Valida si el nombre de un archivo es apto para subirse directamente a Storage.
 * Comprueba longitud y presencia de caracteres conflictivos como '+', '%', '#', etc.
 */
export function validateSafeFileName(fileName: string): FileValidationResult {
  const trimmed = fileName.trim();

  if (!trimmed) {
    return {
      isValid: false,
      error: 'El nombre del archivo no puede estar vacío.',
    };
  }

  if (trimmed.length > MAX_SAFE_FILE_NAME_LENGTH) {
    return {
      isValid: false,
      error: `El nombre del archivo es demasiado largo (${trimmed.length} caracteres, máx. ${MAX_SAFE_FILE_NAME_LENGTH}). Por favor, renómbralo en tu equipo con un nombre más simple antes de adjuntarlo.`,
      suggestedName: sanitizeToSafeFileName(trimmed),
    };
  }

  if (FORBIDDEN_FILE_NAME_CHARS_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error: 'El nombre del archivo contiene caracteres no permitidos (como "+", "&", "#", etc.). Por favor, renómbralo en tu equipo con un nombre más simple (solo letras, números, guiones y puntos) antes de adjuntarlo.',
      suggestedName: sanitizeToSafeFileName(trimmed),
    };
  }

  if (/\.\./.test(trimmed)) {
    return {
      isValid: false,
      error: 'El nombre del archivo contiene puntos consecutivos (".."). Por favor, renómbralo con un nombre más simple antes de adjuntarlo.',
      suggestedName: sanitizeToSafeFileName(trimmed),
    };
  }

  return { isValid: true };
}
